import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Search } from 'lucide-react';
import { cn } from '../lib/cn';

// Command palette building blocks (CO-0042), on cmdk like the Command component of shadcn/ui.

export const Command = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof CommandPrimitive>>(({ className, ...p }, ref) => (
  <CommandPrimitive ref={ref} className={cn('flex h-full w-full flex-col overflow-hidden bg-surface text-fg-1', className)} {...p} />
));
Command.displayName = 'Command';

export function CommandDialog({
  open,
  onOpenChange,
  label,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label: string;
  children: ReactNode;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-navy/50" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-[15dvh] z-50 w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-lg border border-line-subtle shadow-lg"
        >
          <DialogPrimitive.Title className="sr-only">{label}</DialogPrimitive.Title>
          <Command label={label} loop>
            {children}
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export const CommandInput = forwardRef<HTMLInputElement, ComponentPropsWithoutRef<typeof CommandPrimitive.Input>>(
  ({ className, ...p }, ref) => (
    <div className="flex items-center gap-2 border-b border-line-subtle px-3">
      <Search className="size-4 shrink-0 text-fg-3" aria-hidden />
      <CommandPrimitive.Input
        ref={ref}
        className={cn('h-12 w-full bg-transparent text-sm outline-none placeholder:text-fg-4', className)}
        {...p}
      />
    </div>
  ),
);
CommandInput.displayName = 'CommandInput';

export const CommandList = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof CommandPrimitive.List>>(
  ({ className, ...p }, ref) => (
    <CommandPrimitive.List ref={ref} className={cn('max-h-[50dvh] overflow-y-auto p-1', className)} {...p} />
  ),
);
CommandList.displayName = 'CommandList';

export const CommandEmpty = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>>((p, ref) => (
  <CommandPrimitive.Empty ref={ref} className="py-6 text-center text-sm text-fg-3" {...p} />
));
CommandEmpty.displayName = 'CommandEmpty';

export const CommandGroup = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof CommandPrimitive.Group>>(
  ({ className, ...p }, ref) => (
    <CommandPrimitive.Group
      ref={ref}
      className={cn('[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:text-fg-3', className)}
      {...p}
    />
  ),
);
CommandGroup.displayName = 'CommandGroup';

export const CommandItem = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof CommandPrimitive.Item>>(
  ({ className, ...p }, ref) => (
    <CommandPrimitive.Item
      ref={ref}
      className={cn(
        'flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-2 text-sm outline-none',
        'data-[selected=true]:bg-sunken data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 [&_svg]:size-4 [&_svg]:text-fg-3',
        className,
      )}
      {...p}
    />
  ),
);
CommandItem.displayName = 'CommandItem';

export const CommandShortcut = ({ children }: { children: ReactNode }) => (
  <span className="ml-auto font-mono text-xs text-fg-3">{children}</span>
);
